import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@env';
import { Store } from '@ngrx/store';
import { combineLatest, delay, of, Subject, takeUntil } from 'rxjs';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { getCurrentOrganizationId$ } from '../../store/selectors/session.selector';
import {
    getLeftNavCategory$,
    getLeftNavCategoryWithSearch$,
    getLeftNavMap$,
    getLeftNavSearchText$,
} from '../../store/selectors/shared.selector';
import { getTaxonomyApp$ } from '../../store/selectors/apps.selector';
import { SetSearchedValue } from '../../store/actions/shared.actions';
import { UtilsService } from '../../services/utils/utils.service';

import { SharedState } from '../../store/states';

@Component({
    selector: 'app-categories',
    templateUrl: './categories.component.html',
    styleUrls: ['./categories.component.scss'],
    standalone: false,
})
export class CategoriesComponent implements OnInit, OnDestroy {
    @Input() selectedLeftNav: SharedState['leftNavMap'][0];
    @Output() closeMenu = new EventEmitter();
    @Output() bridgeDialogOpen = new EventEmitter<boolean>();
    private unSubscribe = new Subject<void>();
    public currentOrganizationId: string;
    public packages = [];
    public apps = [];
    public activeCategory: string;
    public filterAppName = '';
    public isNewUI: boolean;
    public showTaxonomy: boolean = environment?.['showTaxonomy'];

    constructor(
        private store$: Store,
        private _router: Router,
        private _route: ActivatedRoute,
        private _commonUtils: CommonUtilsService,
        private _utils: UtilsService
    ) {
        this.isNewUI = this._commonUtils.getCookie('new-ui') === 'true';
    }

    public searchPackage = (event: Event) => {
        const searchText = (event.target as HTMLInputElement).value ? (event.target as HTMLInputElement).value : '';
        this.store$.dispatch(SetSearchedValue({ searchText }));
        if (this.selectedLeftNav && searchText?.length > 0) {
            this.store$
                .select(getLeftNavCategoryWithSearch$(this.selectedLeftNav))
                .pipe(takeUntil(this.unSubscribe))
                .subscribe(leftNav => {
                    this.packages = leftNav?.packages ?? [];
                    this.apps = leftNav?.apps ?? [];
                });
        }
    };

    public clearSearchApp = () => {
        this.filterAppName = '';
        this.store$.dispatch(SetSearchedValue({ searchText: '' }));
    };

    private navigate = (tab: string) => {
        if (!location.href.includes(this.currentOrganizationId)) {
            return;
        }
        const url = '/organizations/' + this.currentOrganizationId + '/category/' + tab;
        this._router.navigate([url]);
    };

    public closeMatmenu = () => {
        this.closeMenu.emit();
    };

    public trackById = (index, item) => {
        return item?.id || item?.serviceId || index;
    };

    ngOnInit() {
        if (!this.isNewUI) {
            this._utils.navigateToHomePage();
            return;
        }
        this.store$
            .select(getCurrentOrganizationId$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(event => {
                this.currentOrganizationId = event;
            });
        if (this.selectedLeftNav) {
            this.filterAppName = '';
            this.store$
                .select(getLeftNavCategoryWithSearch$(this.selectedLeftNav))
                .pipe(takeUntil(this.unSubscribe))
                .subscribe(leftNav => {
                    this.packages = (!environment.restrictedProcessingUIs.includes('gst-filing') && (leftNav?.packages ?? [])) || [];
                    this.apps = leftNav?.apps ?? [];
                });
        } else {
            const leftNavCategory$ = this.store$.select(getLeftNavCategory$) || of(undefined);
            const leftNavMap$ = this.store$.select(getLeftNavMap$) || of(undefined);

            combineLatest([this._route.params, leftNavCategory$, leftNavMap$])
                .pipe(takeUntil(this.unSubscribe), delay(10))
                .subscribe(([params, category, navItems]) => {
                    this.activeCategory = category?.id;
                    if (params?.category) {
                        this.packages = (!environment.restrictedProcessingUIs.includes('gst-filing') && (category?.packages ?? [])) || [];
                        this.apps = category?.apps ?? [];
                    } else if (navItems?.[0]?.id) {
                        this.navigate(navItems?.[0]?.id);
                    }
                });
        }
        this.store$
            .select(getTaxonomyApp$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(data => {
                this.showTaxonomy = data?.serviceId?.length > 0;
            });
        this.store$
            .select(getLeftNavSearchText$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(searchText => {
                this.filterAppName = searchText;
            });
    }

    ngOnDestroy(): void {
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }
}
